import { useEffect, useRef } from "react"

import { Breadcrumb, Link } from "@/atoms"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { Heading } from "@/molecules/Heading"
import { HeaderPage } from "@/organisms/HeaderPage"
import { FormPackageRegistry } from "@/pages/IntegrationsPage/components/molecules/FormPackageRegistry"
import { TestPackageRegistryButton } from "@/pages/IntegrationsPage/components/molecules/TestPackageRegistryButton"
import { useIntegration } from "@/pages/IntegrationsPage/hooks/useIntegration"
import { usePackageRegistryAPI } from "@/pages/IntegrationsPage/hooks/usePackageRegistryAPI"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"
import { ConnectIntegrationFooter } from "../ConnectIntegrationPage/ConnectIntegrationFooter"

import styles from "./PackageRegistryIntegrationPage.module.scss"

export const PackageRegistryIntegrationPage = () => {
  const { t } = useTranslation()
  const { logger, EVENTS } = useLogger()
  const vendor = INTEGRATION_VENDORS.packageRegistry
  const [_, { onRemove: _onRemove, ...formProps }] = useIntegration({ vendor })
  const registryApi = usePackageRegistryAPI(formProps)
  const title = t("integrations.packageRegistry.title")
  const logged = useRef(false)

  useEffect(() => {
    if (logged.current) return

    logged.current = true
    logger.info(
      EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.PACKAGE_REGISTRY.ADD_STARTED
    )
  }, [logger, EVENTS])

  return (
    <PageWithBreadcrumbTemplate
      full
      header={
        <HeaderPage
          title={
            <Breadcrumb
              items={[
                {
                  title: (
                    <Link to="/integrations">
                      {t("navigation.integrations")}
                    </Link>
                  )
                },
                {
                  title: (
                    <Link to={formProps.getBackToIntegrationUrl()}>
                      Private Package Registry
                    </Link>
                  )
                },
                { title }
              ]}
            />
          }
        />
      }
      footer={
        <ConnectIntegrationFooter
          disabled={!formProps.isDirty || registryApi.isTesting}
          isLoading={registryApi.isPending}
          okText={t("integrations.packageRegistry.form.buttons.add")}
          okProps={{ form: registryApi.formId, htmlType: "submit" }}
          onCancel={() => formProps.backToIntegration()}
        >
          <TestPackageRegistryButton
            tKey="integrations.packageRegistry.form.buttons.test"
            form={formProps.form}
            t={t}
            registryApi={registryApi}
          />
        </ConnectIntegrationFooter>
      }
    >
      <div className={styles.title}>
        <Heading level={4} title={title} />
        <Heading
          level={6}
          title={t("integrations.packageRegistry.form.title")}
        />
      </div>
      <FormPackageRegistry
        formId={registryApi.formId}
        onFinish={registryApi.onFinish}
        {...formProps}
      />
    </PageWithBreadcrumbTemplate>
  )
}
